import type { ChipTone } from '@/components/common/StatusChip';
import type { InstallmentStatus } from '@/types/shared';

/** Chip tone per installment status — mirrors `lib/studentStatus.ts`'s pattern. `Overdue` is a
 * read-time-derived overlay (never persisted, see accounts.enum.ts) but still just another tone
 * here. */
const TONE_BY_INSTALLMENT_STATUS: Record<InstallmentStatus, ChipTone> = {
  Pending: 'neutral',
  Paid: 'success',
  Overdue: 'danger',
  'Partially Paid': 'warning',
};

export function toneForInstallmentStatus(status: InstallmentStatus): ChipTone {
  return TONE_BY_INSTALLMENT_STATUS[status] ?? 'neutral';
}

/** A6: only a fully `Paid` installment locks its amount/dueDate — Partially Paid rows stay
 * editable, same as Pending/Overdue. */
export function isInstallmentLocked(status: InstallmentStatus): boolean {
  return status === 'Paid';
}

/** Account-level payment progress — a coarser summary chip than the per-installment status,
 * shown on the account summary card and the list's progress cue. */
export type AccountProgress = 'paid' | 'partial' | 'pending';

export function accountProgress(totalPaid: number, totalAgreedFees: number): AccountProgress {
  if (totalAgreedFees > 0 && totalPaid >= totalAgreedFees) return 'paid';
  if (totalPaid > 0) return 'partial';
  return 'pending';
}

const TONE_BY_PROGRESS: Record<AccountProgress, ChipTone> = {
  paid: 'success',
  partial: 'warning',
  pending: 'neutral',
};

export function toneForAccountProgress(progress: AccountProgress): ChipTone {
  return TONE_BY_PROGRESS[progress];
}

/** Decimal-safe cents conversion for the client-side D6 live check — never compare money as
 * floats (mirrors accounts.mapper.ts `toCents` on the server). */
export function toCents(amount: number): number {
  return Math.round(amount * 100);
}
